import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiLogoYoutube } from 'react-icons/bi';

import { useGetInterfaceMessageQuery } from '@/app/services/chatbot-rtk.services';

import { PSButton } from '../ui/button';

type AlertProps = {
  image_link?: string;
  video_link?: string;
  message_date_time: string; // ISO string or Date
  message?: string;
  cta: string;
  cta_target?: string;
  level?: 'info' | 'warning' | 'error';
};

const DashboardAlert = () => {
  const { data: alertData, isLoading, isError, isSuccess, refetch } = useGetInterfaceMessageQuery();
  const { i18n } = useTranslation();
  const [alerts, setAlerts] = useState<AlertProps[]>([]);

  useEffect(() => {
    if (i18n.language && isSuccess && alertData) {
      // Get the alerts in the correct language
      const languageKey = i18n.language.startsWith('en') ? 'en' : 'fr';
      if (alertData[languageKey] && alertData[languageKey].length > 0) {
        setAlerts(alertData[languageKey]);
      }
    }
  }, [alertData, i18n.language, isSuccess]);

  if (isLoading) {
    return <div className="tw-p-4">Loading...</div>;
  }

  if (isError) {
    return (
      <div className="tw-p-4">
        Error happened... <PSButton id="btn-dashboard-refetch" onClick={refetch} title="Retry" />
      </div>
    );
  }

  if (alerts.length === 0) {
    return null;
  }

  return (
    <div className="tw-w-full tw-bg-white tw-mt-2">
      {alerts.map((alert, index) => (
        <React.Fragment key={index}>
          <div className="tw-grid lg:tw-grid-cols-[70px_1fr_auto] tw-gap-4 tw-p-4 tw-items-center">
            {/* Media Section - Fixed width */}
            <div className="tw-flex tw-justify-center tw-items-center">{renderMedia(alert)}</div>

            {/* Message Section */}
            <div className="tw-text-gray-800 tw-text-start tw-font-medium tw-font-['Lexend'] tw-leading-normal tw-text-sm">{alert.message}</div>

            {/* CTA Section */}
            <div className="tw-flex tw-items-center tw-justify-end">
              {alert.cta && (
                <a
                  style={{ whiteSpace: 'break-spaces' }}
                  href={alert.cta_target ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`tw-px-4 tw-py-2 ${
                    alert.level === 'info' ? 'tw-bg-blue-700' : alert.level === 'warning' ? 'tw-bg-yellow-700' : 'tw-bg-red-700'
                  } tw-rounded-md tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-whitespace-nowrap`}
                >
                  {alert.cta}
                </a>
              )}
            </div>
          </div>

          {/* Ligne horizontale - pas sur le dernier élément */}
          {index < alerts.length - 1 && <hr className="tw-border-gray-300" />}
        </React.Fragment>
      ))}
    </div>
  );
};

const renderMedia = (item: AlertProps) => {
  if (item.image_link) {
    return (
      <a href={item.cta_target ?? '#'} target="_blank" rel="noopener noreferrer" className="tw-block">
        <img className="tw-w-[40px] tw-object-cover" src={item.image_link} alt="Alert" />
      </a>
    );
  }

  if (item.video_link) {
    return (
      <a href={item.video_link} target="_blank" rel="noopener noreferrer" className="tw-block">
        <BiLogoYoutube size={50} className="tw-text-red-500" />
      </a>
    );
  }

  return <div className="tw-w-[50px] tw-h-[50px]"></div>;
};

export default DashboardAlert;
